import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { BasicInfoComponent } from './steps/basic-info/basic-info.component';
import { DistributionComponent } from './steps/distribution/distribution.component';
import { DocumentationComponent } from './steps/documentation/documentation.component';
import { CreatorProfileComponent } from './steps/creator-profile/creator-profile.component';
import { CompensationComponent } from './steps/compensation/compensation.component';
import { CreateBriefService } from '../briefs/service/create-brief/create-brief.service';
import { Location } from '@angular/common';
import { AlertService } from '../../atoms/alert/alert.service';
import { BriefResponseDto } from '../../common/general-types/briefs.dto';
import { Router } from '@angular/router';
import { PosthogService } from '../../services/analytics/posthog.service';
import { LoadingOverlayComponent } from '../../atoms/loading-overlay/loading-overlay.component';

@Component({
  selector: 'app-create-brief-agent',
  standalone: true,
  imports: [CommonModule, BasicInfoComponent, DistributionComponent,
    DocumentationComponent, CreatorProfileComponent, CompensationComponent,
    LoadingOverlayComponent
  ],
  templateUrl: './create-brief-agent.component.html',
  styleUrl: './create-brief-agent.component.css',
  animations: [
    trigger('stepSlide', [
      transition(':increment', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':decrement', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ])
    ])
  ]
})
export class CreateBriefAgentComponent implements OnInit {

  briefSteps: { id: number, name: string, status: -1 | 0 | 1 }[] = [
    { id: 0, name: 'Basic Info', status: 0 },
    { id: 1, name: 'Distribution', status: -1 },
    { id: 2, name: 'Documentation', status: -1 },
    { id: 3, name: 'Creator Profile', status: -1 },
    { id: 4, name: 'Compensation', status: -1 },
  ];

  currentStep = signal<number>(0);

  showBriefBusyCreating = signal<boolean>(false);

  /** Services */
  private router = inject(Router);
  private location = inject(Location);
  public createBriefService = inject(CreateBriefService);
  public alertService = inject(AlertService);
  private posthogService = inject(PosthogService);
  /** Services - END */

  ngOnInit(): void {
    this.createBriefService.resetBriefBuilder();
    this.initializeWizard();
  }

  private initializeWizard(): void {
    this.briefSteps[0].status = 0;
    for (let i = 1; i < this.briefSteps.length; i++) {
      this.briefSteps[i].status = -1;
    }
    this.currentStep.set(0);
    this.createBriefService.getPricingModelOptions();
  }

  nextStep() {
    const stepComplete: { complete: boolean; message: string } = this.createBriefService.getStepCompleteAgent(this.currentStep());

    if (!stepComplete.complete) {
      this.alertService.alert({
        title: 'Please trust the process and complete each step to continue.',
        message: stepComplete.message,
        confirmText: 'I trust!',
        cancelText: 'Cancel',
        type: 'alert',
        icon: 'info',
      });
      return;
    }

    const lastStepIndex = this.briefSteps.length - 1;

    if (this.currentStep() === lastStepIndex) {
      // User is on the last step
      this.onBriefReady();
      return;
    }

    this.briefSteps[this.currentStep()].status = 1;
    if (this.briefSteps[this.currentStep() + 1]) {
      this.briefSteps[this.currentStep() + 1].status = 0;
    }
    this.currentStep.set(this.currentStep() + 1);
  }

  onBriefReady() {
    console.log('Ready to post!');

    this.showBriefBusyCreating.set(true);

    switch (this.createBriefService.pricingUserType()) {
      case 'admin':
        this.createPaymentMethod();
        break;
      case 'shout_plus':
        this.createPaymentMethod();
        break;
      case 'regular':
        this.submitBrief();
        break;
      default:
        break;
    }
  }

  async createPaymentMethod() {
    const paymentMethodCreated = await this.createBriefService.createPaymentMethod();
    if (!paymentMethodCreated) {
      this.alertService.alert({
        title: 'Error creating payment method',
        message: 'Please try again.',
        confirmText: 'OK',
        cancelText: 'Cancel',
        type: 'alert',
        icon: 'info',
      });
      return;
    } else {
      this.submitBrief();
    }
  }

  submitBrief() {
    console.log('Submitting brief...');
    this.createBriefService.sendFinalBrief().subscribe((res: BriefResponseDto) => {
      console.log('Brief created: ', res);
      this.createBriefService.resetBriefBuilder();
      this.initializeWizard();
      this.posthogService.posthog.capture('brief_created', {
        brief_id: res.id,
        brief_title: (res as any).briefTitle || (res as any).title,
        pricing_user_type: this.createBriefService.pricingUserType(),
      });

      this.showBriefBusyCreating.set(false);
      this.alertService.confirm({
        title: 'Brief created successfully!',
        message: 'You can now view your brief in the briefs page.',
        confirmText: 'Manage Brief',
        cancelText: 'Back to Briefs',
        type: 'confirm',
        icon: 'success',
      }).then((result) => {
        console.log('result: ', result);
        if (result) {
          this.router.navigate(['/manage-brief', res.id, 2]);
        } else {
          this.location.back();
        }
      })

    }, (error) => {
      this.showBriefBusyCreating.set(false);
      this.createBriefService.clearProgressSimulation();
      this.createBriefService.progress_indicator.set(0);
      console.error('Error creating brief: ', error);
      this.alertService.alert({
        title: 'Whoops! Something went wrong.',
        message: 'Please try again.',
        confirmText: 'OK',
        cancelText: 'Cancel',
        type: 'alert',
        icon: 'info',
      });
    });
  }

  isLastStep(): boolean {
    return this.currentStep() === this.briefSteps.length - 1;
  }

  previousStep() {
    this.briefSteps[this.currentStep()].status = -1;
    if (this.briefSteps[this.currentStep() - 1]) {
      this.briefSteps[this.currentStep() - 1].status = 0;
    }
    this.currentStep.set(this.currentStep() - 1);
  }

  getBriefStepClass(step: { id: number, name: string, status: -1 | 0 | 1 }) {
    switch (step.status) {
      case 0:
        return 'border-primary dark:bg-primary/20 bg-primary/10 text-accent';
      case -1:
        return 'border-zinc-500 dark:bg-zinc-900/30 bg-zinc-50 dark:text-zinc-300 text-zinc-500';
      case 1:
        return 'border-green-500 dark:bg-green-900/30 bg-green-50 dark:text-green-300 text-green-500';
    }
  }

  getStepCircleClass(step: { id: number, name: string, status: -1 | 0 | 1 }): string {
    switch (step.status) {
      case 1:
        return 'bg-primary border-primary text-white';
      case 0:
        return 'bg-white dark:bg-gray-800 border-primary text-accent';
      case -1:
        return 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400';
    }
  }

  getStepLabelClass(step: { id: number, name: string, status: -1 | 0 | 1 }): string {
    switch (step.status) {
      case 1:
        return 'text-accent';
      case 0:
        return 'text-accent';
      case -1:
        return 'text-gray-500 dark:text-gray-400';
    }
  }

  getConnectorClass(currentStep: { id: number, name: string, status: -1 | 0 | 1 }, nextStep: { id: number, name: string, status: -1 | 0 | 1 } | undefined): string {
    if (currentStep.status === 1) {
      return 'bg-primary dark:bg-primary';
    }
    return 'bg-gray-300 dark:bg-gray-600';
  }

  backToBriefs() {
    this.location.back();
  }
}
